import { useEffect, useRef, useState } from "react";
import { Bell, Flame, Heart, Mail, X } from "lucide-react";
import { api } from "../lib/api";

/**
 * NotificationsBell — dropdown showing hot-lead alerts (users who crossed
 * 3+ saved homes). Polls every 30 seconds so the badge auto-refreshes.
 */
export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/notifications", {
        withCredentials: true,
      });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // poll every 30s
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/admin/notifications/${id}/read`, {}, { withCredentials: true });
      load();
    } catch {
      /* ignore */
    }
  };

  const markAll = async () => {
    try {
      await api.post("/admin/notifications/read-all", {}, { withCredentials: true });
      load();
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center justify-center w-10 h-10 bg-white border border-gray-200 hover:border-[#E63946] rounded-md transition-colors"
        aria-label="Notifications"
        data-testid="admin-notifications-bell"
      >
        <Bell className="w-4 h-4 text-[#1A1A1A]" />
        {unread > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 bg-[#E63946] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center leading-none"
            data-testid="admin-notifications-unread"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 w-[380px] max-h-[520px] bg-white border border-gray-200 rounded-xl shadow-xl z-50 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="text-sm font-semibold text-[#1A1A1A] flex items-center gap-2">
              <Flame className="w-4 h-4 text-[#E63946]" /> Hot-lead alerts
            </div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  onClick={markAll}
                  className="text-[10px] uppercase tracking-[0.15em] text-gray-500 hover:text-[#E63946]"
                  data-testid="admin-notifications-read-all"
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-[#1A1A1A]">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">
                <Bell className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                No alerts yet. When a visitor saves 3+ homes, they&apos;ll appear here.
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => !n.read && markRead(n.id)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-stone-50 transition-colors ${
                    !n.read ? "bg-red-50/50" : ""
                  }`}
                  data-testid={`admin-notification-${n.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        n.read ? "bg-stone-100 text-gray-500" : "bg-[#E63946] text-white"
                      }`}
                    >
                      <Heart className="w-4 h-4" fill={n.read ? "none" : "white"} strokeWidth={n.read ? 1.75 : 0} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[#1A1A1A] truncate">
                        {n.user_name || n.user_email || "Anonymous"} · {n.saved_count} homes saved
                      </div>
                      {n.user_email && (
                        <a
                          href={`mailto:${n.user_email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-[#E63946] hover:underline flex items-center gap-1 mt-0.5"
                        >
                          <Mail className="w-3 h-3" /> {n.user_email}
                        </a>
                      )}
                      {n.latest_listing?.address && (
                        <div className="text-xs text-gray-600 mt-1 truncate">
                          Just saved: {n.latest_listing.address}
                          {n.latest_listing.city ? `, ${n.latest_listing.city}` : ""}
                          {n.latest_listing.price
                            ? ` — $${Number(n.latest_listing.price).toLocaleString()}`
                            : ""}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[10px] text-gray-400">
                          {new Date(n.created_at).toLocaleString()}
                        </span>
                        {n.email_status === "sent" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700">
                            emailed
                          </span>
                        )}
                        {n.email_status === "mocked" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">
                            email mocked
                          </span>
                        )}
                      </div>
                    </div>
                    {!n.read && (
                      <div className="w-2 h-2 rounded-full bg-[#E63946] shrink-0 mt-2" />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
